// ─────────────────────────────────────────────────────────────────────────────
// SETTING AN ORGANISATION'S LOOK. OURS, NOT THEIRS.
//
// Not a customer preference pane. It is a control WE use, on a customer's behalf,
// according to what their people prefer — one chain lives in PointClickCare all day and
// wants the dense clinical workstation, another's DON prints everything and wants the
// survey exhibit. Gated to the platform owner and the VP role on both sides.
//
// The gate on THIS side is a convenience, not a control. Anything a browser can hide, a
// browser can unhide; the route is what actually says no, and it is mounted behind the
// facility scope check so a VP can only ever reach their own customer.
//
// ── THE THING THIS SCREEN MUST NOT GET WRONG ────────────────────────────────
//
// The setting is PER ORGANISATION and the control sits on a FACILITY screen — you open one
// building and change every building in the chain. A control that does not say so is a
// trap, so the count comes back from the server with the answer and is printed on the
// button itself. Not "Save", but "Change the look for all 9 facilities".
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from "react";

import { DENSITIES, PRESETS, presetList } from "./presets.js";
import { applyTheme } from "./applyTheme.js";
import ThemeThumb from "./ThemeThumb.jsx";

// ── CURRENT AND SELECTED ARE TWO DIFFERENT FACTS ────────────────────────────
//
// The grid used to mark one card, with `aria-pressed`, and that one mark had to mean
// both "this is the look they have" and "this is the look you are about to give them".
// Those are the same card only until you click a second one, and after that the screen
// no longer says what you are changing FROM. So each card carries at most one badge:
//
//   LIVE NOW    the organisation's stored look — what a user of theirs sees right now
//   CHANGING TO the pending choice, not saved, which the button below then commits
//   (both)      you clicked back to where you started — so "LIVE NOW · NO CHANGE"
//
// Derived from `theme` against `resolved.theme` on every render rather than stored,
// because a count or a badge that ignores what was just clicked is the bug this
// codebase keeps writing.
export function badgeFor(key, selected, current) {
  const isSel = key === selected;
  const isCur = key === current;
  if (isSel && isCur) return { text: "Live now · no change", tone: "quiet" };
  if (isSel) return { text: "Changing to this", tone: "loud" };
  if (isCur) return { text: "Live now", tone: "quiet" };
  return null;
}

/**
 * @param {object}   props
 * @param {string}   props.ccn       the facility the control was opened from
 * @param {string}   [props.orgName] the organisation's name, for the sentence
 * @param {object}   props.resolved  the GET answer: {theme, density, source, why, facilities}
 * @param {Function} props.onSave    (body) => Promise<{ok, error?}>
 * @param {boolean}  [props.busy]
 */
export default function ThemePicker({ ccn, orgName, resolved, onSave, busy = false }) {
  const [theme, setTheme] = useState(resolved?.theme);
  const [density, setDensity] = useState(resolved?.density);
  const [status, setStatus] = useState(null);

  // The answer can arrive after the first render — the modal opens, then the GET lands.
  // Without this the control would sit on its first guess and quietly offer to save it.
  useEffect(() => {
    setTheme(resolved?.theme);
    setDensity(resolved?.density);
  }, [resolved]);

  // APPLIED LIVE, SO THE CHOICE CAN BE JUDGED BEFORE IT IS SAVED. A preset is a whole
  // visual language and no swatch conveys it; the only honest preview is the actual
  // screen. `explicit` outranks everything, so this is a true preview and is discarded on
  // the next ordinary applyTheme — nothing is persisted by looking.
  const preview = (key) => applyTheme({ explicit: key });

  const count = Number.isFinite(resolved?.facilities) ? resolved.facilities : null;
  const dirty = theme !== resolved?.theme || density !== resolved?.density;
  // The chosen preset's NAME, for the button. `PRESETS[theme]` rather than `presetFor`
  // on purpose: a key we do not recognise must not be printed as "Editorial" on a button
  // that is about to store the unrecognised key. It falls back to the plain sentence.
  const themeName = PRESETS[theme]?.label || null;

  const save = async () => {
    setStatus(null);
    const res = await onSave({ theme, density });
    setStatus(res?.ok ? { ok: true, text: "Saved." } : { ok: false, text: res?.error || "Could not save." });
  };

  const label = { fontSize: 11, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--ed-muted)" };

  return (
    <section
      aria-labelledby="theme-picker-heading"
      style={{
        border: "1px solid var(--ed-line)",
        borderRadius: "var(--ed-r-lg)",
        padding: 16,
        marginTop: 20,
        background: "var(--ed-line-2)",
      }}
    >
      <h3 id="theme-picker-heading" style={{ margin: "0 0 2px", fontSize: 14, fontWeight: 800, color: "var(--ed-ink)" }}>
        Look for {orgName || "this organisation"}
      </h3>
      {/* THE SENTENCE THAT STOPS THIS BEING A TRAP. The control is on a facility, the
          setting is on the chain, and only one of those is obvious from where you are. */}
      <p style={{ margin: "0 0 12px", fontSize: 12.5, lineHeight: 1.5, color: "var(--ed-ink-2)" }}>
        This is set for the whole organisation
        {count !== null ? <strong> — all {count} {count === 1 ? "facility" : "facilities"}</strong> : null}, not just the
        one you opened.{" "}
        {resolved?.source === "organization"
          ? "A look is stored for them already."
          : "Nothing is stored yet, so they see the standard look."}
        {resolved?.why ? <span style={{ color: "var(--ed-muted)" }}> ({resolved.why})</span> : null}
      </p>

      <div style={{ ...label, marginBottom: 6 }}>Theme</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 10, marginBottom: 14 }}>
        {presetList().map((p) => {
          const on = theme === p.key;
          const badge = badgeFor(p.key, theme, resolved?.theme);
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => { setTheme(p.key); preview(p.key); }}
              onMouseEnter={() => preview(p.key)}
              onMouseLeave={() => preview(theme)}
              aria-pressed={on}
              style={{
                textAlign: "left",
                display: "block",
                padding: 9,
                borderRadius: "var(--ed-r-md)",
                border: `${on ? 2 : 1}px solid ${on ? "var(--ed-brand)" : "var(--ed-line-hover)"}`,
                // The selected card pulls in by the extra border pixel rather than
                // growing, so choosing a theme does not reflow the grid under the cursor.
                margin: on ? 0 : 1,
                background: on ? "var(--ed-brand-soft)" : "var(--ed-canvas)",
                color: "var(--ed-ink-2)",
                font: "inherit",
                cursor: "pointer",
              }}
            >
              {/* THE MINIATURE, drawn from this preset's own tokens. A theme is a ground,
                  a rule weight, a radius and a pair of faces; a bar of brand colour was
                  showing one of the five. */}
              <ThemeThumb tokens={PRESETS[p.key].tokens} />

              {/* THE NAME, always visible and never only a tooltip — a name you have to
                  hover to read is not a name you can choose by. */}
              <span
                style={{
                  display: "block",
                  marginTop: 7,
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: on ? "var(--ed-brand)" : "var(--ed-ink)",
                }}
              >
                {p.label}
              </span>

              {badge ? (
                <span
                  style={{
                    display: "inline-block",
                    marginTop: 4,
                    padding: "1px 7px",
                    borderRadius: "var(--ed-r-pill)",
                    fontSize: 9.5,
                    fontWeight: 800,
                    letterSpacing: ".05em",
                    textTransform: "uppercase",
                    // Loud only for the pending change, because that is the one the
                    // button below is about to act on. The stored look is a fact, not
                    // a call to action.
                    background: badge.tone === "loud" ? "var(--ed-brand)" : "var(--ed-line-2)",
                    color: badge.tone === "loud" ? "var(--ed-canvas)" : "var(--ed-muted)",
                    border: `1px solid ${badge.tone === "loud" ? "var(--ed-brand)" : "var(--ed-line-hover)"}`,
                  }}
                >
                  {badge.text}
                </span>
              ) : null}

              <span style={{ display: "block", marginTop: 4, fontSize: 11, lineHeight: 1.4, color: "var(--ed-muted)", fontWeight: 400 }}>
                {p.description}
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ ...label, marginBottom: 6 }}>Density</div>
      <div role="radiogroup" aria-label="Density" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {Object.values(DENSITIES).map((d) => {
          const on = density === d.key;
          return (
            <button
              key={d.key}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setDensity(d.key)}
              style={{
                padding: "7px 14px",
                borderRadius: "var(--ed-r-pill)",
                border: `1px solid ${on ? "var(--ed-brand)" : "var(--ed-line-hover)"}`,
                background: on ? "var(--ed-brand-soft)" : "var(--ed-canvas)",
                color: on ? "var(--ed-brand)" : "var(--ed-ink-2)",
                font: "inherit",
                fontSize: 12.5,
                fontWeight: on ? 700 : 500,
                cursor: "pointer",
              }}
            >
              {d.label}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        {/* THE SCOPE IS ON THE BUTTON, not only in the paragraph above it. The paragraph is
            what you read once; the button is what you read as you click. */}
        {/* THE SCOPE AND THE DESTINATION, both on the button. The badge in the grid says
            which card you picked; this says what clicking does to whom, named — so the
            last thing read before committing is "all 9 facilities to Survey exhibit"
            rather than a bare "Save". */}
        <button type="button" className="btn-primary" onClick={save} disabled={busy || !dirty} style={{ fontSize: 13 }}>
          {busy
            ? "Saving…"
            : count !== null && count > 1
              ? themeName
                ? `Change all ${count} facilities to ${themeName}`
                : `Change the look for all ${count} facilities`
              : themeName
                ? `Change the look to ${themeName}`
                : "Change the look"}
        </button>
        <span style={{ fontSize: 12, color: "var(--ed-muted)" }}>{dirty ? "Not saved yet." : "Nothing to save."}</span>
        {status ? (
          <span role="status" style={{ fontSize: 12.5, fontWeight: 600, color: status.ok ? "var(--ed-success)" : "var(--ed-alert)" }}>
            {status.text}
          </span>
        ) : null}
      </div>

      <p style={{ margin: "12px 0 0", fontSize: 11.5, lineHeight: 1.5, color: "var(--ed-muted)" }}>
        Only the platform owner and a VP can see this control, and only they can save it — the
        server refuses the write for anyone else. Hovering a theme previews it on this screen
        for real; nothing is stored until you save. Each card is drawn from its own theme's
        ground, rule and corners, but only the theme in use loads its typeface, so an
        unchosen card shows the right <em>kind</em> of face rather than the exact one — hover
        it to see that. The chart check's four marks never change, whichever look is chosen:
        a blocked column has to keep reading as blocked.
      </p>
      <p style={{ margin: "6px 0 0", fontSize: 11.5, color: "var(--ed-muted)" }}>
        Opened from facility {ccn}
        {resolved?.orgId ? ` · organisation ${resolved.orgId}` : ""}
      </p>
    </section>
  );
}
